// src/components/scenes/ProfileScene.js
// Filepath: // src/components/scenes/ProfileScene.js
import React, { useEffect, useMemo } from "react";
import { Box, Card, CardContent, Typography, Avatar, Paper } from "@mui/material";
import { useUser } from "../../state/UserContext";
import { useScene } from "../../state/SceneContext";
import { setSceneHandlers } from "../../input/remote";
import Hero from "../common/Hero";
import Focusable from "../common/Focusable";
import QRPlaceholder from "../common/QRPlaceholder";
import { DEMO_USERS } from "../../data/demoUsers";

export default function ProfileScene() {
  const { user } = useUser();
  const { setScene } = useScene();
  const meId = "u1"; // mock “current” account id

  useEffect(() => {
    setSceneHandlers({ onBack: () => { setScene({ name: "home" }); return true; } });
    return () => setSceneHandlers({});
  }, [setScene]);

  const stats = useMemo(() => ({
    followers: 1280, following: 342, createdVideos: 9, createdImages: 9,
    purchasedVideos: 24, purchasedImages: 24, watchlist: 24,
  }), []);

  const displayName = user?.displayName ?? DEMO_USERS[0].name;
  const handle = user?.username ?? DEMO_USERS[0].handle;
  const bio = user?.bio ?? "Creator • Collector • Explorer. Building the next wave of micro content with Kbean.";
  const banner = user?.bannerUrl ?? DEMO_USERS[0].banner;
  const avatar = user?.avatar ?? DEMO_USERS[0].avatar;

  // How much vertical room we reserve for the hero area
  const HERO_RESERVE = "56vh"; // tweak as you like (should be > Hero textTop)

  const routes = [
    { label: "Followers", value: stats.followers, onClick: () => setScene({ name: "followers", params: { mode: "followers", userId: meId } }) },
    { label: "Following", value: stats.following, onClick: () => setScene({ name: "followers", params: { mode: "following", userId: meId } }) },
    { label: "Watch List", value: stats.watchlist, onClick: () => setScene({ name: "grid", params: { source: "watchlist", backTo: "profile", sceneKey: "grid-watchlist" } }) },
    { label: "Created Videos", value: stats.createdVideos, onClick: () => setScene({ name: "grid", params: { source: "published", backTo: "profile", sceneKey: "grid-created-videos" } }) },
    { label: "Created Images", value: stats.createdImages, onClick: () => setScene({ name: "grid", params: { source: "published", backTo: "profile", sceneKey: "grid-created-images" } }) },
    { label: "Purchased Videos", value: stats.purchasedVideos, onClick: () => setScene({ name: "grid", params: { source: "purchases", backTo: "profile", sceneKey: "grid-purchased-videos" } }) },
    { label: "Purchased Images", value: stats.purchasedImages, onClick: () => setScene({ name: "grid", params: { source: "purchases", backTo: "profile", sceneKey: "grid-purchased-images" } }) },
  ];

  return (
    <Box
      className="scene"
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        p: 2,
        position: "relative",
        zIndex: 1, // scene is above the hero background (which is zIndex:0)
      }}
      data-rails-root
    >
      {/* HEADER LAYER (reserves space for Hero) */}
      <Box
        sx={{
          position: "relative",
          minHeight: HERO_RESERVE, // pushes the next section down below the hero area
          zIndex: 1, // above hero background
        }}
      >
        <Hero
          title={displayName}
          subtitle={`${handle} • ${bio}`}
          backgroundUrl={banner}
          // Leave Hero as-is; it is fixed + zIndex:0 (background) + its own title at zIndex:1
        />

        {/* Avatar & quick header bits above hero */}
        <Box
          sx={{
            position: "absolute",
            left: 24,
            top: 24,
            display: "flex",
            alignItems: "center",
            gap: 2,
            zIndex: 2, // sit over hero title if they overlap near the top
            pointerEvents: "auto",
          }}
        >
          <Avatar
            src={avatar}
            sx={{
              width: 96,
              height: 96,
              border: "3px solid rgba(255,255,255,0.65)",
              boxShadow: 3,
            }}
          />
        </Box>
      </Box>

      {/* CONTENT LAYER (cards: Followers / Following / etc.) */}
      <Box
        sx={{
          position: "relative",
          bottom: "20px",
          zIndex: 2, // ensure content is above hero (z0) and also above hero title (z1)
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: 2,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1.5,
            alignContent: "start",
          }}
        >
          {routes.map((s, i) => (
            <Focusable key={s.label} row={100 + Math.floor(i / 3)} col={i % 3} onClick={s.onClick}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  borderRadius: "10px",
                  bgcolor: "background.paper",
                  display: "grid",
                  alignContent: "center",
                  minHeight: 120,
                }}
              >
                <Typography variant="h2" sx={{ lineHeight: 1 }}>
                  {Intl.NumberFormat().format(s.value)}
                </Typography>
                <Typography sx={{ color: "text.secondary", mt: 0.5 }}>
                  {s.label}
                </Typography>
              </Paper>
            </Focusable>
          ))}
        </Box>

        <Card sx={{ borderRadius: "10px", alignSelf: "start" }}>
          <CardContent>
            <Typography variant="h2">Edit on your phone</Typography>
            <Typography sx={{ color: "text.secondary", mb: 2 }}>
              Scan to manage your profile in Kbean Studio.
            </Typography>
            <Box sx={{ display: "grid", placeItems: "center", my: 1 }}>
              <QRPlaceholder text="Scan to edit profile" />
            </Box>
            <Typography sx={{ color: "text.secondary" }}>
              Name: {displayName}
              <br />
              Handle: {handle}
              <br />
              Email: {user?.email ?? "you@example.com"}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
